import { useCallback } from 'react'
import { useLocalForageKey } from '../../../../utils/local-storage/useLocalForageKey.hooks'
import { SESSION_DATA_IDENTIFIER } from './sessionToken.utils'

type SessionData = {
  token?: string
}

/**
 * Hook for managing session data with dual-write to both localforage and localStorage.
 *
 * This hook is part of the migration from localforage to localStorage:
 * - Reads come from localforage (current source of truth)
 * - Writes go to both localStorage and localforage
 *
 * Once migration is complete, this hook can be updated to read from localStorage.
 */
export const useSessionData = () => {
  const {
    data,
    loading,
    setData: setLocalForageData,
    hasKey,
  } = useLocalForageKey<SessionData>(SESSION_DATA_IDENTIFIER)

  const setData = useCallback(
    (value: SessionData | undefined) => {
      // Write to localStorage for migration
      if (value !== undefined && value !== null) {
        localStorage.setItem(SESSION_DATA_IDENTIFIER, JSON.stringify(value))
      } else {
        localStorage.removeItem(SESSION_DATA_IDENTIFIER)
      }
      // Write to localforage (still source of truth for reads)
      setLocalForageData(value as SessionData)
    },
    [setLocalForageData]
  )

  return { data, loading, setData, hasKey }
}
